<template>
  <div class="fixed inset-0 overflow-hidden z-50">
    <div class="absolute inset-0 overflow-hidden">
      <section class="absolute inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16" aria-labelledby="slide-over-heading">
        <div class="w-screen max-w-2xl">
          <div class="h-full flex flex-col bg-white shadow-xl">
            <div class="min-h-0 flex-1 flex flex-col overflow-y-scroll">
              <!-- Header -->
              <div class="px-4 py-6 sm:px-6" :class="'bg-' + block.color + '-100'">
                <div class="flex items-center justify-between space-x-3">
                  <div class="flex-none">
                    <div class="items-center w-10 h-10 rounded-full" :class="'bg-' + block.color + '-200'">
                      <BlockIcon
                        v-bind:type="faceType"
                        v-bind:color="color"
                        v-bind:isPreview="true"
                      ></BlockIcon>
                    </div>
                  </div>
                  <div class="space-y-1 flex-grow">
                    <h2 class="text-lg font-medium text-gray-900">{{ block.title }}</h2>
                    <div class="text-xs text-gray-600">
                      {{ doneCount }}/{{ totalCount }} done
                      <span v-if="streakCount" class="ml-2">· streak {{ streakCount }}</span>
                    </div>
                  </div>
                  <div class="h-7 flex-none items-center">
                    <button
                      @click="blockEditStop"
                      class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      type="button"
                    >
                      <span class="sr-only">Close panel</span>
                      <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="border-b border-gray-200 mt-6">
                <div class="px-6">
                  <nav class="-mb-px flex space-x-6">
                    <a
                      @click="mode = 'info'"
                      href="#"
                      class="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm"
                      :class="{ 'border-indigo-500 text-indigo-600 hover:text-indigo-600 hover:border-indigo-500': mode === 'info' }"
                    >
                      Info
                    </a>
                    <a
                      @click="mode = 'todos'"
                      href="#"
                      class="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm"
                      :class="{ 'border-indigo-500 text-indigo-600 hover:text-indigo-600 hover:border-indigo-500': mode === 'todos' }"
                    >
                      ToDos
                    </a>
                  </nav>
                </div>
              </div>

              <!-- Info -->
              <div v-if="mode==='info'" class="py-6 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                <div class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <div>
                    <label for="block_title" class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2">Title</label>
                  </div>
                  <div class="sm:col-span-2">
                    <input v-model="title" type="text" name="block_title" id="block_title" class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md">
                  </div>
                </div>

                <div class="space-y-2 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:px-6 sm:py-5">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Color</h3>
                  </div>
                  <div class="sm:col-span-2">
                    <div
                      v-for="colorOption in colors"
                      :key="colorOption.value"
                      class="inline-block rounded-full h-10 w-10 m-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 shadow border-white border-2"
                      :class="'bg-' + colorOption.value + '-400 hover:bg-' + colorOption.value + '-700 focus:ring-' + colorOption.value + '-500'"
                      @click="color = colorOption.value"
                    >
                      <svg v-if="colorOption.value === color" className="inline-block h-6 w-6 rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                  </div>
                </div>

                <div class="space-y-2 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:px-6 sm:py-5">
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Icon</h3>
                  </div>
                  <div class="sm:col-span-2">
                    <BlockIconPicker
                      v-bind:type="faceType"
                      @block-icon-update="blockChangeFace"
                    />
                  </div>
                </div>

                <div class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <div>
                    <label class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2">Reset</label>
                  </div>
                  <div class="sm:col-span-2">
                    <button
                      @click="resetList"
                      type="button"
                      class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      :disabled="!todosLocal.length"
                    >
                      Reset this list
                    </button>
                    <div class="mt-1 text-xs text-gray-500">
                      Saves a cycle entry to history (for streaks).
                    </div>
                  </div>
                </div>
              </div>

              <!-- Todos -->
              <div v-else class="py-6 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                <div class="space-y-1 px-4 sm:px-6 sm:py-5">
                  <div class="py-2 align-middle inline-block min-w-full">
                    <div class="shadow border-b border-gray-200 sm:rounded-lg">
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                          <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Text</th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Done</th>
                            <th scope="col" class="relative px-6 py-3"><span class="sr-only">Actions</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr class="bg-white" v-for="(todo, index) in todosLocal" :key="todo.id">
                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <input v-model="todo.text" type="text" class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md" />
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <input v-model="todo.done" type="checkbox" class="h-4 w-4" />
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <a @click="todoDelete(index)" href="#" class="text-indigo-600 hover:text-indigo-900">Delete</a>
                            </td>
                          </tr>
                          <tr class="bg-white">
                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <input v-model="todoNewText" type="text" class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md" placeholder="New todo" />
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"></td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <a @click="todoAdd" href="#" class="text-indigo-600 hover:text-indigo-900">Add</a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="px-4 sm:px-6 sm:py-5">
                  <h3 class="text-sm font-medium text-gray-900">History</h3>
                  <div v-if="!historyLocal.length" class="text-sm text-gray-500 mt-1">
                    No resets yet.
                  </div>
                  <div v-else class="text-sm text-gray-700 mt-1">
                    Last: {{ historyLocal[historyLocal.length - 1].completed }}/{{ historyLocal[historyLocal.length - 1].total }}
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="flex-shrink-0 px-4 py-4 flex justify-end space-x-2">
                <button
                  @click="blockEditStop"
                  type="button"
                  class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  @click="blockUpdate"
                  type="button"
                  class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {colors} from '../colors/colors.js';
import BlockIcon from './BlockIcon.vue';
import BlockIconPicker from './BlockIconPicker.vue';
import {v4 as uuidv4} from 'uuid';

function normalizeTodos(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const id = t.id ? String(t.id) : uuidv4();
      const text = typeof t.text === 'string' ? t.text : '';
      const done = !!t.done;
      const doneAt = typeof t.doneAt === 'number' ? t.doneAt : null;
      if (!text) return null;
      return { id, text, done, doneAt };
    })
    .filter(Boolean);
}

function normalizeHistory(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((h) => {
      if (!h || typeof h !== 'object') return null;
      const ts = typeof h.ts === 'number' ? h.ts : Date.now();
      const total = typeof h.total === 'number' ? h.total : 0;
      const completed = typeof h.completed === 'number' ? h.completed : 0;
      return { ts, total, completed };
    })
    .filter(Boolean);
}

export default {
  name: 'BlockTodoListEdit',
  components: {
    BlockIcon,
    BlockIconPicker,
  },
  props: {
    block: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      mode: 'info',
      colors,

      title: this.block.title,
      color: this.block.color,
      faceType: this.block.faceType ? String(this.block.faceType) : '2705',

      todosLocal: normalizeTodos(this.block.todos),
      historyLocal: normalizeHistory(this.block.todoHistory),

      todoNewText: '',
    };
  },
  computed: {
    doneCount() {
      return this.todosLocal.filter(t => t.done).length;
    },
    totalCount() {
      return this.todosLocal.length;
    },
    streakCount() {
      const h = this.historyLocal;
      if (!h.length) return 0;
      let streak = 0;
      for (let i = h.length - 1; i >= 0; i--) {
        const entry = h[i];
        if (!entry.total) break;
        if (entry.completed !== entry.total) break;
        streak++;
      }
      return streak;
    },
  },
  methods: {
    blockEditStop() {
      this.$emit('blockEditStop');
    },
    blockChangeFace(face) {
      this.faceType = String(face);
    },
    todoAdd() {
      const text = String(this.todoNewText || '').trim();
      if (!text) return;
      this.todosLocal.push({ id: uuidv4(), text, done: false, doneAt: null });
      this.todoNewText = '';
    },
    todoDelete(index) {
      this.todosLocal.splice(index, 1);
    },
    resetList() {
      const total = this.todosLocal.length;
      const completed = this.todosLocal.filter(t => t.done).length;
      this.historyLocal.push({ ts: Date.now(), total, completed });
      if (this.historyLocal.length > 50) this.historyLocal.splice(0, this.historyLocal.length - 50);

      this.todosLocal.forEach((t) => {
        t.done = false;
        t.doneAt = null;
      });
    },
    blockUpdate() {
      const data = {};

      if (this.title !== this.block.title) data.title = JSON.parse(JSON.stringify(this.title));
      if (this.color !== this.block.color) data.color = JSON.parse(JSON.stringify(this.color));
      if (this.faceType !== this.block.faceType) data.faceType = JSON.parse(JSON.stringify(this.faceType));

      if (JSON.stringify(this.todosLocal) !== JSON.stringify(this.block.todos)) {
        data.todos = JSON.parse(JSON.stringify(this.todosLocal));
      }
      if (JSON.stringify(this.historyLocal) !== JSON.stringify(this.block.todoHistory)) {
        data.todoHistory = JSON.parse(JSON.stringify(this.historyLocal));
      }

      if (Object.keys(data).length) {
        this.$emit('blockUpdate', this.block.refID, this.block.id, data);
      }
      this.blockEditStop();
    },
  },
};
</script>
